import { get, writable } from 'svelte/store';
import { parseResponse } from '$lib/client/responseHelpers';

import type { Favorite } from '@api/types';
import { getFavoritedByPaginated, getFavoritesPaginated } from '@api/user';
import { USER_TIMEOUT_MS } from '@shared/constants';

import type { Store, StoreState } from './types';

const initialValues: StoreState = {
    myFavorites: [],
    favoritedBy: [],
};

function createStore(): Store {
    const store = writable(initialValues);
    const { subscribe, update, set } = store;
    let timer: number;

    const updateStore = (data: Partial<StoreState>) => {
        update((state) => ({
            ...state,
            ...data,
        }));
    };

    const loadMyFavorites = async (force?: boolean, limit?: number) => {
        const storeState = get(store);
        // refresh last favorites by lastActivityAt
        if (timer) {
            window.clearTimeout(timer);
        }
        if (
            (storeState.myFavorites.length && storeState.myFavoritesNextCursor)
            || !storeState.myFavorites.length
            || force
        ) {
            const listResponse = await getFavoritesPaginated(
                force ? undefined : storeState.myFavoritesNextCursor,
                limit
            );
            const data = parseResponse(listResponse);

            if (data) {
                if (force) {
                    if (data.favorites.length
                        && storeState.myFavorites.length
                        && data.favorites[0].id === storeState.myFavorites[0].id
                    ) {
                        return;
                    }
                    updateStore({
                        myFavorites: data.favorites,
                        myFavoritesNextCursor: data.nextCursor,
                    });
                } else {
                    updateStore({
                        myFavorites: [...storeState.myFavorites, ...data.favorites],
                        myFavoritesNextCursor: data.nextCursor,
                    });
                }

                if (limit && !data.nextCursor) {
                    return;
                }
            }
        }

        if (limit) {
            timer = window.setTimeout(() => loadMyFavorites(true, limit), USER_TIMEOUT_MS);
        }
    };

    const loadFavoritedBy = async () => {
        const storeState = get(store);
        if ((storeState.favoritedBy.length && storeState.favoritedByNextCursor) || !storeState.favoritedBy.length) {
            const listResponse = await getFavoritedByPaginated(storeState.favoritedByNextCursor);
            const data = parseResponse(listResponse);

            if (data) {
                updateStore({
                    favoritedBy: [...storeState.favoritedBy, ...data.favorites],
                    favoritedByNextCursor: data.nextCursor,
                });
            }
        }
    };

    return {
        update,
        set,
        subscribe,
        loadMyFavorites,
        loadFavoritedBy,
        addFavorite: (favorite:Favorite) => {
            const currentStore = get(store);
            updateStore({
                myFavorites: [
                    favorite,
                    ...currentStore.myFavorites,
                ],
            });
            const favoritedBy = currentStore.favoritedBy.find((fav) => fav.id === favorite.id);
            if (favoritedBy) {
                favoritedBy.inMyFavorites = true;
            }
        },
        deleteFavorite: (id: string) => {
            const currentStore = get(store);
            updateStore({
                myFavorites: currentStore.myFavorites.filter(item => item.id !== id),
            });
            const favoritedBy = currentStore.favoritedBy.find((fav) => fav.id === id);
            if (favoritedBy) {
                favoritedBy.inMyFavorites = false;
            }
        },

        changeIsBlocked: (id: string, val: boolean) => {
            const currentStore = get(store);

            if (val) {
                updateStore({
                    myFavorites: currentStore.myFavorites.filter(item => item.id !== id),
                });
            } else {
                const favorite = currentStore.myFavorites.find((fav) => fav.id === id);
                if (favorite) {
                    favorite.isBlocked = val;
                }
            }

            const favoritedBy = currentStore.favoritedBy.find((fav) => fav.id === id);
            if (favoritedBy) {
                favoritedBy.isBlocked = val;
                favoritedBy.inMyFavorites = false;
            }
        },

        changeIsBlockedBy: (id: string, val: boolean) => {
            const currentStore = get(store);
            const favorite = currentStore.myFavorites.find((fav) => fav.id === id);
            if (favorite) {
                favorite.isBlockedBy = val;
            }

            if (val) {
                updateStore({
                    favoritedBy: currentStore.favoritedBy.filter(item => item.id !== id),
                });
            } else {
                const favoritedBy = currentStore.favoritedBy.find((fav) => fav.id === id);
                if (favoritedBy) {
                    favoritedBy.isBlockedBy = val;
                }
            }
        },
    };
}

export const favoritesStore = createStore();

